import React, { useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { makeStyles } from '@mui/styles';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import _uniq from 'lodash/uniq';
import { Button, Divider, Grid } from '@mui/material';

import * as ENDPOINT from 'config/endpoint';
import { shortName } from 'utils/names';
import moment, { getMomentDate } from 'utils/moment';
import FormInputDisplay from 'components/FormElements/FormInputDisplay';
import { appStoreIsLoading, appStoreMakeRequest } from 'reactStore/slices/appStoreSlice';
import MPaper from 'components/MaterialUI/MPaper';
import MGrid from 'components/MaterialUI/MGrid';
import MText from 'components/MaterialUI/MText';
import { IconTrain } from 'assets/icons';
import { getRubles } from 'utils/helpers';
import OrderPassengers from 'pages/Orders/RailwayOrders/RailwayOrderPassengers/OrderPassengers';

const useStyles = makeStyles((theme) => ({
  icon: {
    width: 32,
    height: 32,
    color: '#FFF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.grey5.main,
    borderRadius: '50%',
  },
  open: {
    border: `1px solid ${theme.palette.info.main}`,
  },
}));

const RailwayOrdersListCard = (props) => {
  const {
    rgdOrderListStore,
    order,
    order: { ['order_items']: orderItems = [] },
  } = props;

  const storeName = 'rgd-order-list-store';
  const mapper = useCallback(
    (res, old) => old.map((i) => (i['id'] !== order?.id ? i : { ...i, ...res.data.data })),
    [order?.id],
  );

  const dispatch = useDispatch();

  const { originalPrice, totalOrderAmount } = orderItems.reduce(
    (amount, { reservation }) => {
      const totalOrderAmount = reservation?.actual_sell_price?.order_item_total?.gross?.cents || 0;
      const originalPrice = reservation?.initial_sell_price?.provider_total?.gross?.cents || 0;

      return {
        totalOrderAmount,
        originalPrice,
      };
    },
    {
      totalOrderAmount: 0,
      originalPrice: 0,
    },
  );

  const orderWays = Object.values(
    orderItems?.reduce((total, current) => {
      const result = current?.reservation?.raw_data?.booking_data?.reservation_items?.find(
        (reservationItem) => !total[reservationItem.origin_station_code],
      );

      if (!!result) {
        return { ...total, [result.origin_station_code]: { ...result } };
      }

      return total;
    }, {}),
  ).sort((a, b) => (!moment(b.local_departure_date_time).isAfter(moment(a.local_departure_date_time)) ? 1 : -1));

  const classes = useStyles();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const reservationState = orderItems?.[0]?.['reservation']?.['state'];
  const allowConfirm = ['booked'].includes(reservationState);
  const isConfirmedOrderState = orderItems?.some(
    (i) =>
      i?.['reservation']?.['state'] === 'confirmed' ||
      i?.['reservation']?.['state'] === 'in_refund' ||
      i?.['reservation']?.['state'] === 'pending_for_refund',
  );
  const isAllPaid = orderItems.every(({ payment_intent }) => payment_intent?.state === 'paid');

  const updateOrderItem = async () => {
    const dataSource = `${ENDPOINT.ORDERS}/${order.id}/actualize`;
    setLoading(true);
    await dispatch(appStoreMakeRequest({ storeName, dataSource, mapper, method: 'PATCH' }));
  };

  const ordersListStoreIsLoading = useSelector(appStoreIsLoading({ storeName }));
  const isLoading = rgdOrderListStore && loading;

  useEffect(() => {
    if (!ordersListStoreIsLoading) {
      setLoading(false);
    }
  }, [ordersListStoreIsLoading]);

  const getNames = () => {
    return orderItems.reduce((result, orderItem) => {
      return [...result, shortName(orderItem.traveller)];
    }, []);
  };

  const getOrderStatus = () => {
    const orderItems = order?.['order_items'];
    // Выполнены
    const everyOrdersBooked = orderItems?.every((item) => item?.['reservation']?.['state'] === 'booked');
    const everyOrdersConfirmed = orderItems?.every((item) => item?.['reservation']?.['state'] === 'confirmed');
    const everyOrdersBookedAndConfirmed =
      orderItems?.some((item) => item?.['reservation']?.['state'] === 'booked') &&
      orderItems?.some((item) => item?.['reservation']?.['state'] === 'confirmed');

    const everyOrdersConfirmedAndRefunded =
      orderItems?.some((item) => item?.['reservation']?.['state'] === 'confirmed') &&
      orderItems?.some((item) => item?.['reservation']?.['state'] === 'refunded');

    const everyOrdersCancelledAndConfirmed =
      orderItems?.some((item) => item?.['reservation']?.['state'] === 'cancelled') &&
      orderItems?.some((item) => item?.['reservation']?.['state'] === 'confirmed');

    const everyOrdersCancelledAndBooked =
      orderItems?.some((item) => item?.['reservation']?.['state'] === 'cancelled') &&
      orderItems?.some((item) => item?.['reservation']?.['state'] === 'booked');

    // Отменены
    const everyOrdersRefunded = order?.['order_items']?.every(
      (item) => item?.['reservation']?.['state'] === 'refunded',
    );
    const everyOrdersCanceled = order?.['order_items']?.every(
      (item) => item?.['reservation']?.['state'] === 'cancelled',
    );
    const everyOrdersUnconfirmed = order?.['order_items']?.every(
      (item) => item?.['reservation']?.['state'] === 'unconfirmed',
    );

    // Ожидает подтверждения
    const someOrdersConfirmationPending = order?.['order_items']?.some(
      (item) => item?.['reservation']?.['state'] === 'confirmation_pending',
    );

    if (order?.['state']) {
      if (
        (everyOrdersBooked ||
          everyOrdersConfirmed ||
          everyOrdersBookedAndConfirmed ||
          everyOrdersConfirmedAndRefunded ||
          everyOrdersCancelledAndConfirmed ||
          everyOrdersCancelledAndBooked) &&
        !someOrdersConfirmationPending
      )
        return 'Выполнен';

      if ((everyOrdersRefunded || everyOrdersCanceled || everyOrdersUnconfirmed) && !someOrdersConfirmationPending)
        return 'Отменен';

      return 'В работе';
    }
    return '-';
  };

  const renderWays = () => {
    const list = orderWays.length ? orderWays : [{ id: 1 }];
    return list.map((reservation, i) => {
      const isFirst = i === 0;

      return (
        <MGrid key={i} size={12} columnSpacing={3}>
          <FormInputDisplay
            {...{ isLoading }}
            size={2}
            label={isFirst && `${t('Orders.Departure')} (время местное)`}
            value={
              reservation['local_departure_date_time']
                ? moment(reservation['local_departure_date_time']).format('DD.MM.YYYY HH:mm')
                : '-'
            }
          />
          <FormInputDisplay
            {...{ isLoading }}
            size={2}
            label={isFirst && `${t('Orders.Arrival')} (время местное)`}
            value={
              reservation['local_arrival_date_time']
                ? moment(reservation['local_arrival_date_time']).format('DD.MM.YYYY HH:mm')
                : '-'
            }
          />
          <FormInputDisplay
            {...{ isLoading }}
            size={2}
            label={isFirst && t('Orders.From')}
            value={reservation['origin_station']}
          />
          <FormInputDisplay
            {...{ isLoading }}
            size={2}
            label={isFirst && t('Orders.To')}
            value={reservation['destination_station']}
          />
          <FormInputDisplay
            {...{ isLoading }}
            size={2}
            label={isFirst && t('Orders.TrainNumber')}
            value={reservation['train_number']}
          />
          <FormInputDisplay
            {...{ isLoading }}
            size={2}
            label={isFirst && t('Orders.CarNumber')}
            value={reservation['car_number']}
          />
        </MGrid>
      );
    });
  };

  return (
    <Grid item xs={12}>
      <MPaper withGrid className={clsx({ [classes.open]: open })} gridProps={{ rowSpacing: 4 }}>
        <MGrid size={12} alignItems="center" justifyContent="space-between">
          <MGrid columnSpacing={6} alignItems="center" justifyContent="flex-start">
            <Grid item children={<div className={classes.icon} children={<IconTrain />} />} />

            <Grid item>
              <MText variant="subtitle1" color="textSecondary" children={`${t('Orders.Order')}:`} />
              <MText {...{ isLoading }} width={76} variant="h3" weight="bold" children={order['code']} />
              <MText {...{ isLoading }} variant="subtitle2" sx={{ color: 'info.main' }} children={getOrderStatus()} />
            </Grid>

            <FormInputDisplay
              {...{ isLoading }}
              label={t('Orders.CreatedAt')}
              value={getMomentDate(order['created_at'])}
            />
            <FormInputDisplay
              {...{ isLoading }}
              label={t('Orders.ChangedAt')}
              value={getMomentDate(order['updated_at'])}
            />
            <FormInputDisplay {...{ isLoading }} label={t('Users.User')} value={order['owner']?.['email']} />
          </MGrid>

          <MGrid rowSpacing={3} columnSpacing={3}>
            {isConfirmedOrderState && (
              <Grid item>
                <Button size="small" variant="text" color="inherit" disabled={isLoading} onClick={updateOrderItem}>
                  {t('Actions.Update')}
                </Button>
              </Grid>
            )}
            <Grid item>
              <Button
                size="small"
                variant="text"
                color="inherit"
                disabled={isLoading}
                onClick={() => {
                  !open && isConfirmedOrderState && updateOrderItem();
                  setOpen((old) => !old);
                }}
              >
                {open ? t('Actions.Hide') : t('Actions.Show')}
              </Button>
            </Grid>
          </MGrid>
        </MGrid>

        <Grid item xs={12} children={<Divider />} />

        <MGrid size={12} columnSpacing={6} justifyContent="flex-start">
          <Grid item children={<div style={{ width: 32 }} />} />
          <MGrid rowSpacing={3} direction="column">
            <Grid item>
              <MText variant="subtitle1" color="textSecondary" children={`${t('Orders.ToPay')}:`} />
              <MText {...{ isLoading }} variant="h3" weight="bold" children={getRubles(totalOrderAmount)} />
            </Grid>
            <FormInputDisplay {...{ isLoading }} label={t('Orders.Payed')} value={isAllPaid ? 'Да' : 'Нет'} />
          </MGrid>

          <MGrid size={10} rowSpacing={3}>
            <FormInputDisplay {...{ isLoading }} label={t('Orders.Passengers')} value={_uniq(getNames()).join(' ')} />
            <MGrid size={12} children={renderWays()} />

            {open && (
              <MGrid size={12}>
                <FormInputDisplay
                  {...{ isLoading }}
                  size={2}
                  label={t('Orders.OriginalPrice')}
                  value={getRubles(originalPrice)}
                />
                <FormInputDisplay
                  {...{ isLoading }}
                  size={2}
                  label={t('Orders.LeftToPay')}
                  value={isAllPaid ? '0 ₽' : getRubles(Math.abs(totalOrderAmount))}
                />
                <FormInputDisplay {...{ isLoading }} size={2} label={t('Orders.BackSum')} value="-" />
                <FormInputDisplay
                  {...{ isLoading }}
                  size={2}
                  label={t('Orders.Contract')}
                  value={order['contract']?.['number']}
                />
                <FormInputDisplay {...{ isLoading }} size={2} label={t('Orders.InformationAccount')} value="-" />
              </MGrid>
            )}
          </MGrid>
        </MGrid>

        {open && (
          <OrderPassengers
            {...{ orderItems, order }}
            isLoading={isLoading}
            orderId={order['id']}
            orderState={order['state']}
            orderCode={order['code']}
            createdData={order['created_at']}
            fromCode={orderWays[0]?.origin_station_code}
            toCode={orderWays[1]?.origin_station_code}
            contractId={order?.['contract']?.['id']}
            allowConfirm={allowConfirm}
            ownerId={order?.owner?.id}
          />
        )}
      </MPaper>
    </Grid>
  );
};

RailwayOrdersListCard.propTypes = {
  rgdOrderListStore: PropTypes.bool,
  order: PropTypes.object,
};

RailwayOrdersListCard.defaultProps = {
  order: {},
};

export default RailwayOrdersListCard;
